import { useCallback } from 'react';
import {
  lastLoggedSetFromEntry,
  type LastLoggedSet,
  type LogSet,
} from '@heraion/shared';

import { lastSessionEntryFromLogSets } from './lastSessionFromLogSets';
import {
  getLastLoggedSetForSlug,
  useEntryHistory,
  useEntryHistoryApiFetch,
} from './useEntryHistory';
import { NO_FETCH } from './useExerciseHistoryView';
import { useExerciseSetsFallback } from './useExerciseSetsFallback';

const indexLastLogged = (sets: readonly LogSet[] | null) => {
  const last = sets ? lastSessionEntryFromLogSets(sets) : null;
  return last ? lastLoggedSetFromEntry(last) : undefined;
};

/** The athlete's last logged set on `slug`, as a reader. Extracted from
 *  useWeightCandidates (#539: the AMRAP test reads it too). The window holds
 *  50 sessions: an exercise resumed after a break is not in it, so the index
 *  is asked only when the window is blind, as the history view does; `ready`
 *  matters because before the window lands, empty is not an answer. The
 *  reader changes identity when the index answers, so a memo can depend on
 *  it. */
export function useLastLoggedSet(slug: string): () => LastLoggedSet | undefined {
  const { ready } = useEntryHistory(slug);
  const apiFetch = useEntryHistoryApiFetch();
  const windowLast = slug !== '' ? getLastLoggedSetForSlug(slug) : null;
  const fallback = useExerciseSetsFallback(
    slug,
    slug !== '' && ready && apiFetch !== null && windowLast === null,
    apiFetch ?? NO_FETCH,
  );
  const fallbackSets = fallback.sets;
  return useCallback(
    () =>
      (slug !== '' ? getLastLoggedSetForSlug(slug) : null) ??
      indexLastLogged(fallbackSets) ??
      undefined,
    [slug, fallbackSets],
  );
}
